customElements.whenDefined('search-form').then(() => {
  const cache = new Map();
  let instanceCount = 0;

  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.statusElement = this.querySelector('.predictive-search-status');
      this.instanceId = `keysrus-search-${++instanceCount}`;
      this.requestId = 0;
      this.isOpen = false;
      this.searchTerm = '';
      this.input.setAttribute('aria-controls', `${this.instanceId}-predictive-search-results`);
      this.input.removeAttribute('aria-owns');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;
      this.onViewportChange = () => { if (this.isOpen) this.fitResults(); };
      window.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('scroll', this.onViewportChange);
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('input', event => {
        // Invalidate immediately, including during the debounce and IME composition.
        this.close();
        if (!event.isComposing) this.inputTimer = setTimeout(() => this.onChange(), 250);
      });
      this.input.addEventListener('compositionend', () => this.onChange());
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('focusout', () => setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }));
      this.addEventListener('click', event => {
        if (event.target.closest('.inline-search__close-button')) {
          this.close();
          this.input.blur();
        }
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      clearTimeout(this.inputTimer);
      this.inputTimer = null;
      this.searchTerm = this.getQuery();
      if (!this.searchTerm || !this.contains(document.activeElement)) return this.close();
      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery()) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      this.searchTerm = '';
      this.close();
    }

    onFocus() {
      if (window.updateViewportSize) window.updateViewportSize();
      this.onChange();
    }

    onKeydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        if (this.isOpen || this.hasAttribute('loading') || this.inputTimer) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
      } else if (event.target === this.input && this.isOpen) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.switchOption(event.key === 'ArrowUp' ? -1 : 1);
        } else if (event.key === 'Enter') {
          const selected = this.predictiveSearchResults.querySelector('[aria-selected="true"] a');
          if (selected) {
            event.preventDefault();
            selected.click();
          }
        }
      }
    }

    switchOption(direction) {
      const options = [...this.predictiveSearchResults.querySelectorAll('[role="option"]')].filter(option => option.getClientRects().length);
      if (!options.length) return;
      const previous = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
      const next = previous < 0 ? (direction > 0 ? 0 : options.length - 1) : (previous + direction + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }

    async getSearchResults(searchTerm) {
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      const requestId = ++this.requestId;
      const intent = window.KeysrusSearch?.plan(searchTerm) || { original: searchTerm, query: searchTerm, kind: 'native', refinements: [] };
      this.intent = intent;
      const params = new URLSearchParams();
      this.querySelector('template[data-predictive-search-config]')?.content.querySelectorAll('input').forEach(input => params.set(input.name, input.value));
      params.set('q', intent.query);
      let endpoint = `${routes.predictive_search_url}?${params}`;
      if (window.KeysrusSearch) endpoint = window.KeysrusSearch.urlFor(intent.query, { section_id: 'keysrus-search-suggestions', ...(intent.filterVendor ? { 'filter.p.vendor': intent.filterVendor } : {}) }).href;
      const isCurrent = () => requestId === this.requestId && searchTerm === this.getQuery() && this.contains(document.activeElement);
      this.setAttribute('loading', 'true');
      this.predictiveSearchResults.setAttribute('aria-busy', 'true');
      this.setLiveRegionText(this.dataset.loadingText);
      const timeout = setTimeout(() => controller.abort(), 8000);
      let rendered = false;
      let relatedMarkup = null;
      const showRelated = () => {
        if (rendered && relatedMarkup && isCurrent()) this.renderRelatedResults(relatedMarkup);
      };
      const products = this.fetchSearchMarkup(endpoint, controller.signal).then(async markup => {
        // An interpreted query with few matches (for example a partial part number while typing) is
        // topped up from Shopify's native prefix search instead of showing "No results".
        if (intent.kind !== 'native' && isCurrent()) {
          const found = (markup.match(/id="predictive-search-option-product-/g) || []).length;
          if (found < 3) {
            const nativeEndpoint = window.KeysrusSearch.urlFor(searchTerm, { section_id: 'keysrus-search-suggestions' }).href;
            const nativeMarkup = await this.fetchSearchMarkup(nativeEndpoint, controller.signal);
            markup = found ? this.mergeProductSuggestions(markup, nativeMarkup) : nativeMarkup;
          }
        }
        if (!isCurrent()) return;
        this.renderSearchResults(markup);
        rendered = true;
        showRelated();
      }).catch(() => {
        if (!isCurrent()) return;
        this.renderFallback(searchTerm);
        rendered = this.isOpen;
        showRelated();
      });
      let related = Promise.resolve();
      if (window.KeysrusSearch) {
        params.set('q', searchTerm);
        params.set('resources[type]', 'query,collection,page');
        const relatedEndpoint = `${routes.predictive_search_url}?${params}`;
        related = this.fetchSearchMarkup(relatedEndpoint, controller.signal).then(markup => {
          relatedMarkup = markup;
          showRelated();
        }).catch(() => {});
      }
      try {
        await Promise.all([products, related]);
      } finally {
        clearTimeout(timeout);
      }
    }

    async fetchSearchMarkup(endpoint, signal) {
      if (cache.has(endpoint)) return cache.get(endpoint);
      const response = await fetch(endpoint, { signal });
      if (!response.ok) throw Error('Search unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = doc.querySelector('#shopify-section-keysrus-search-suggestions, #shopify-section-predictive-search');
      if (!section?.querySelector('[role="listbox"]')) throw Error('Search response missing');
      const markup = section.innerHTML;
      cache.set(endpoint, markup);
      if (cache.size > 40) cache.delete(cache.keys().next().value);
      return markup;
    }

    mergeProductSuggestions(primaryMarkup, secondaryMarkup) {
      const parse = markup => new DOMParser().parseFromString(`<div>${markup}</div>`, 'text/html').body.firstElementChild;
      const primary = parse(primaryMarkup);
      const list = primary.querySelector('[data-product-search-results]');
      if (!list) return secondaryMarkup;
      const pathOf = item => {
        const href = item.querySelector('a[href]')?.getAttribute('href');
        return href ? new URL(href, location.origin).pathname : null;
      };
      const seen = new Set([...list.children].map(pathOf));
      for (const item of parse(secondaryMarkup).querySelectorAll('[data-product-search-results] > li')) {
        if (list.children.length >= 6) break;
        const path = pathOf(item);
        if (!path || seen.has(path)) continue;
        seen.add(path);
        list.append(item);
      }
      [...list.children].forEach((item, index) => { item.id = `predictive-search-option-product-${index + 1}`; });
      return primary.innerHTML;
    }

    renderRelatedResults(markup) {
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const related = doc.querySelector('.predictive-search__result-group-right');
      const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
      if (!related?.querySelector('[role="option"]') || !listbox) return;
      this.prepareResults(related);
      const wrapper = document.createElement('div');
      wrapper.className = 'predictive-search__results-groups-wrapper keysrus-search-suggestion-groups';
      const products = document.createElement('div');
      products.className = 'predictive-search__result-group';
      [...listbox.children].filter(node => node.matches('h2, [data-product-search-results], .keysrus-search-help')).forEach(node => products.append(node));
      wrapper.append(products, related);
      listbox.insertBefore(wrapper, listbox.querySelector('.keysrus-search-all'));
      const counts = [this.predictiveSearchResults.querySelector('[data-predictive-search-live-region-count-value]')?.textContent, doc.querySelector('[data-predictive-search-live-region-count-value]')?.textContent].filter(Boolean);
      this.setLiveRegionText(counts.join('. '));
      this.fitResults();
    }

    renderFallback(searchTerm) {
      const template = this.querySelector('template[data-predictive-search-fallback]');
      if (!template) return this.close();
      const content = template.content.cloneNode(true);
      const link = content.querySelector('a');
      const url = new URL(this.input.form.action);
      url.search = new URLSearchParams({ q: searchTerm, type: 'product', 'options[prefix]': 'last', 'options[unavailable_products]': 'last' });
      link.href = url.href;
      const holder = document.createElement('div');
      holder.append(content);
      this.renderSearchResults(holder.innerHTML);
    }

    setLiveRegionText(text) {
      if (!this.statusElement) return;
      this.statusElement.removeAttribute('aria-hidden');
      this.statusElement.textContent = text || '';
    }

    renderSearchResults(markup) {
      this.predictiveSearchResults.innerHTML = markup;
      if (this.intent && window.KeysrusSearch) {
        window.KeysrusSearch.humanize(this.predictiveSearchResults, this.intent);
        const guidance = window.KeysrusSearch.guidance(this.intent, { option: true });
        if (guidance.childElementCount) this.predictiveSearchResults.querySelector('[role="listbox"]')?.prepend(guidance);
      }
      this.prepareResults(this.predictiveSearchResults);
      this.input.removeAttribute('aria-activedescendant');
      this.querySelector('.search-suggestions')?.classList.add('hidden');
      this.removeAttribute('loading');
      this.predictiveSearchResults.removeAttribute('aria-busy');
      this.setLiveRegionText(this.predictiveSearchResults.querySelector('[data-predictive-search-live-region-count-value]')?.textContent);
      this.setAttribute('results', 'true');
      this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
      if (window.updateViewportSize) window.updateViewportSize();
      this.fitResults();
    }

    prepareResults(root) {
      const searchPath = new URL(this.input.form.action).pathname;
      root.querySelectorAll('a[href]').forEach(link => {
        const url = new URL(link.href);
        if (url.origin === location.origin && url.pathname === searchPath) {
          url.searchParams.set('type', 'product');
          url.searchParams.set('options[unavailable_products]', 'last');
          link.href = url.href;
        }
      });
      // The same Shopify section can be present in the header and search page.
      const ids = new Map();
      root.querySelectorAll('[id]').forEach(element => {
        const id = `${this.instanceId}-${element.id}`;
        ids.set(element.id, id);
        element.id = id;
      });
      root.querySelectorAll('[aria-labelledby]').forEach(element => {
        element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(/\s+/).map(id => ids.get(id) || id).join(' '));
      });
    }

    fitResults() {
      const top = this.predictiveSearchResults.getBoundingClientRect().top;
      const viewport = window.visualViewport;
      let bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      const previewBar = document.getElementById('PBarNextFrameWrapper');
      if (previewBar?.matches(':popover-open')) bottom = Math.min(bottom, previewBar.getBoundingClientRect().top);
      this.predictiveSearchResults.style.setProperty('max-height', `${Math.max(0, bottom - top - 8)}px`, 'important');
    }

    close(clearSearchTerm = false) {
      clearTimeout(this.inputTimer);
      this.inputTimer = null;
      ++this.requestId;
      this.abortController?.abort();
      if (clearSearchTerm) this.input.value = '';
      this.querySelector('.search-suggestions')?.classList.remove('hidden');
      this.predictiveSearchResults.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.predictiveSearchResults.removeAttribute('aria-busy');
      this.predictiveSearchResults.removeAttribute('style');
      this.setLiveRegionText('');
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      this.close(clearSearchTerm);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
});
